import { useState } from 'react';
import { motion } from 'framer-motion';
import AppLayout from '@/components/feature/AppLayout';
import { usePayments } from '@/hooks/usePayments';
import { Link } from 'react-router-dom';

const statusColor: Record<string, string> = {
  finished: 'bg-green-50 text-green-600',
  waiting_payment: 'bg-amber-50 text-amber-600',
  confirming: 'bg-sky-50 text-sky-600',
  confirmed: 'bg-green-50 text-green-600',
  failed: 'bg-red-50 text-red-500',
  refunded: 'bg-gray-100 text-gray-500',
};

const statusIcon: Record<string, string> = {
  finished: 'ri-checkbox-circle-line',
  waiting_payment: 'ri-time-line',
  confirming: 'ri-loader-4-line',
  confirmed: 'ri-checkbox-circle-line',
  failed: 'ri-close-circle-line',
  refunded: 'ri-arrow-go-back-line',
};

export default function PaymentsPage() {
  const { payments, loading } = usePayments();
  const [selected, setSelected] = useState<(typeof payments)[0] | null>(null);

  const totalSpent = payments
    .filter(p => p.status === 'finished' || p.status === 'confirmed')
    .reduce((s, p) => s + Number(p.amount), 0);

  const successCount = payments.filter(p => p.status === 'finished' || p.status === 'confirmed').length;

  return (
    <AppLayout>
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">Payment History</h1>
          <p className="text-sm text-gray-400 mt-0.5">All your transactions and purchase records</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total Spent', value: loading ? '—' : `$${totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: 'ri-money-dollar-circle-line', color: 'text-brand-500 bg-brand-50' },
            { label: 'Transactions', value: loading ? '—' : payments.length.toString(), icon: 'ri-exchange-line', color: 'text-emerald-500 bg-emerald-50' },
            { label: 'Successful', value: loading ? '—' : successCount.toString(), icon: 'ri-checkbox-circle-line', color: 'text-green-600 bg-green-50' },
          ].map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} className="bg-white rounded-xl p-4">
              <span className={`w-9 h-9 flex items-center justify-center rounded-lg mb-3 ${s.color}`}>
                <i className={`${s.icon} text-lg`} />
              </span>
              <div className="text-xl font-bold text-gray-900">{s.value}</div>
              <div className="text-xs text-gray-400">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Transactions table */}
        <div className="bg-white rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-3">
            <h3 className="font-semibold text-gray-900">Transactions</h3>
          </div>

          {loading ? (
            <div className="p-10 text-center">
              <i className="ri-loader-4-line animate-spin text-2xl text-gray-300 block mb-2" />
              <p className="text-sm text-gray-400">Loading your transactions…</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-16 h-16 bg-surface-2 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-shopping-bag-line text-2xl text-gray-300" />
              </div>
              <h3 className="font-semibold text-gray-700 mb-1">No purchases yet</h3>
              <p className="text-sm text-gray-400 mb-5">Browse our marketplace to find the perfect API for your needs.</p>
              <Link to="/marketplace" className="inline-flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-store-2-line" />Browse Marketplace
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="bg-surface-2 border-b border-surface-3">
                    {['Product', 'Amount', 'Currency', 'Status', 'Date', ''].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <motion.tr
                      key={p.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.04 }}
                      className="border-b border-surface-3 last:border-0 hover:bg-surface-2 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="text-xs font-semibold text-gray-900">{p.product_name}</div>
                        <div className="text-[11px] text-gray-400 font-mono">{p.nowpayments_id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs font-bold text-gray-900">${Number(p.amount).toFixed(2)}</div>
                        <div className="text-[11px] text-gray-400">{p.pay_amount} {p.currency}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold font-mono text-gray-700 bg-surface-2 px-2 py-0.5 rounded">{p.currency}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full capitalize ${statusColor[p.status] ?? 'bg-gray-100 text-gray-500'}`}>
                          <i className={`${statusIcon[p.status] ?? 'ri-question-line'} text-[10px]`} />
                          {p.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[11px] text-gray-400">
                        {new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelected(p)}
                          className="text-xs px-2 py-1 rounded-lg border border-surface-3 text-gray-500 hover:bg-surface-2 cursor-pointer whitespace-nowrap"
                        >
                          Details
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-gray-900">Payment Details</h3>
              <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-2 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <div className="space-y-0">
              {([
                ['Product', selected.product_name],
                ['NP Payment ID', selected.nowpayments_id],
                ['Order ID', selected.order_id ?? '—'],
                ['USD Amount', `$${Number(selected.amount).toFixed(2)}`],
                ['Crypto Amount', `${selected.pay_amount} ${selected.currency}`],
                ['Pay Address', selected.pay_address],
                ['Status', selected.status.replace(/_/g, ' ')],
                ['Date', new Date(selected.created_at).toLocaleString()],
              ] as [string, string][]).map(([k, v]) => (
                <div key={k} className="flex justify-between py-2.5 border-b border-surface-3 last:border-0">
                  <span className="text-xs text-gray-400">{k}</span>
                  <span className="text-xs font-medium text-gray-900 max-w-[220px] truncate text-right">{v}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setSelected(null)} className="w-full mt-4 py-2.5 bg-sidebar text-white rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-sidebar/90">
              Close
            </button>
          </motion.div>
        </div>
      )}
    </AppLayout>
  );
}
